import { useEffect, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { CadNextNotificationTypeEnumType } from '@mark43/cad-api';
import { EntityPanelPersonsForm, PersonFieldsetProps } from '../types';
import { useIntegratedForm } from '../../../../forms/use-integrated-form';

export function useEntityPanelPersonFieldsetState(props: PersonFieldsetProps) {
    const { form: entityForm } = useIntegratedForm<EntityPanelPersonsForm>();

    const { index, baseKey, displayAsCards } = props;
    const [showExtraFields, setShowExtraFields] = useState(false);
    const [showHeightRange, setShowHeightRange] = useState(false);
    const [showWeightRange, setShowWeightRange] = useState(false);
    const [showAgeRange, setShowAgeRange] = useState(false);
    const areRangesSetup = useRef(false);

    const contextId = useWatch({
        control: entityForm.control,
        name: `${props.baseKey}.${index}.contextId`,
    });

    const isPersisted = useWatch({
        control: entityForm.control,
        name: `${props.baseKey}.${index}.isPersisted`,
    });

    const queryPersonOnSave = useWatch({
        control: entityForm.control,
        name: `${props.baseKey}.${index}.queryInDex`,
    });

    const showInvolvementTypeOther = useWatch({
        control: entityForm.control,
        name: `${props.baseKey}.${index}.involvementTypeOption`,
    })?.isOther;

    useEffect(
        function resetInvolvementTypeOther() {
            if (!showInvolvementTypeOther) {
                entityForm.setValue(`${baseKey}.${index}.involvementTypeOther`, null);
            }
        },
        [entityForm, index, baseKey, showInvolvementTypeOther]
    );

    useEffect(
        function showRanges() {
            if (!areRangesSetup.current) {
                const ageRange = entityForm.getValues([
                    `${baseKey}.${index}.ageMax`,
                    `${baseKey}.${index}.ageMin`,
                ]);
                const weightRange = entityForm.getValues([
                    `${baseKey}.${index}.weightMax`,
                    `${baseKey}.${index}.weightMin`,
                ]);
                const heightRange = entityForm.getValues([
                    `${baseKey}.${index}.heightMin`,
                    `${baseKey}.${index}.heightMax`,
                ]);

                if (ageRange.some((r) => !!r)) {
                    setShowAgeRange(true);
                }
                if (weightRange.some((r) => !!r)) {
                    setShowWeightRange(true);
                }
                if (heightRange.some((r) => !!r)) {
                    setShowHeightRange(true);
                }
                areRangesSetup.current = true;
            }
        },
        [baseKey, index, entityForm]
    );

    useEffect(
        function dontQuerySavedEntity() {
            if (isPersisted) {
                // Don't query people that are saved already
                entityForm.setValue(`${baseKey}.${index}.queryInDex`, false);
            }
        },
        [baseKey, entityForm, index, isPersisted]
    );

    const eventNotificationBaseKey =
        baseKey === 'reportingParties' ? 'REPORTING_PARTY' : 'INVOLVED_PERSON';

    const getEventNotificationName = (fieldName: string): CadNextNotificationTypeEnumType =>
        `${eventNotificationBaseKey}_${fieldName}` as CadNextNotificationTypeEnumType;

    function onToggleAgeRange() {
        if (!showAgeRange) {
            entityForm.resetField(`${props.baseKey}.${index}.dateOfBirth`);
        } else {
            entityForm.resetField(`${props.baseKey}.${index}.ageMin`);
            entityForm.resetField(`${props.baseKey}.${index}.ageMax`);
        }
        setShowAgeRange((last) => !last);
    }

    function onToggleHeightRange() {
        if (!showHeightRange) {
            entityForm.resetField(`${props.baseKey}.${index}.height`);
        } else {
            entityForm.resetField(`${props.baseKey}.${index}.heightMax`);
            entityForm.resetField(`${props.baseKey}.${index}.heightMin`);
        }
        setShowHeightRange((last) => !last);
    }

    function onToggleWeightRange() {
        if (!showWeightRange) {
            entityForm.resetField(`${props.baseKey}.${index}.weight`);
        } else {
            entityForm.resetField(`${props.baseKey}.${index}.weightMax`);
            entityForm.resetField(`${props.baseKey}.${index}.weightMin`);
        }
        setShowWeightRange((last) => !last);
    }

    const showDexFields = !displayAsCards || !isPersisted;

    return {
        onToggleAgeRange,
        onToggleHeightRange,
        onToggleWeightRange,
        getEventNotificationName,
        setShowExtraFields,
        showExtraFields,
        queryPersonOnSave,
        isPersisted,
        showHeightRange,
        showWeightRange,
        showAgeRange,
        contextId,
        showDexFields,
        showInvolvementTypeOther,
    };
}
